"use client";
import { AppRoutes } from "@/lib/routeResolver";

import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow } from "swiper/modules";
import type { HomeDestination, TopDestinationSection } from "@/lib/navigation";

import "swiper/css";
import "swiper/css/effect-coverflow";

const DEFAULT_SUB_TITLE = "Top Destination";
const DEFAULT_TITLE = "Popular Destination";

interface DestinationSliderProps {
  destinations: HomeDestination[];
  /** CMS header copy; each field falls back to the static copy when empty. */
  section?: TopDestinationSection;
}

export default function DestinationSlider({
  destinations,
  section,
}: DestinationSliderProps) {
  const subTitle = section?.subTitle || DEFAULT_SUB_TITLE;
  const title = section?.title || DEFAULT_TITLE;

  if (!destinations || destinations.length === 0) return null;

  return (
    <div className="position-relative overflow-hidden mt-5">
      <div className="container">
        <div className="title-area text-center">
          <span className="sub-title">{subTitle}</span>
          <h2 className="sec-title">{title}</h2>
        </div>
        <Swiper
          className="th-slider destination-slider slider-drag-wrap"
          modules={[EffectCoverflow]}
          effect="coverflow"
          coverflowEffect={{
            rotate: 0,
            stretch: 95,
            depth: 212,
            modifier: 1,
          }}
          centeredSlides={true}
          loop={true}
          breakpoints={{
            0: { slidesPerView: 1 },
            576: { slidesPerView: 2 },
            992: { slidesPerView: 3 },
            1200: { slidesPerView: 3 },
          }}
        >
          {destinations.map((dest, i) => (
            <SwiperSlide key={dest.id}>
              <div className={`destination-box${i > 0 ? " gsap-cursor" : ""}`}>
                <div
                  className="destination-img"
                  style={{ position: "relative", height: "350px" }}
                >
                  <Image
                    src={dest.coverPhoto}
                    alt={dest.countryName}
                    fill
                    style={{ objectFit: "cover" }}
                  />
                  <div className="destination-content">
                    <div className="media-left">
                      <h4 className="box-title">
                        <a
                          href={AppRoutes.destination(dest.pageURL)}
                          title={dest.countryName}
                        >
                          {dest.countryName}
                        </a>
                      </h4>
                    </div>
                    <div>
                      <a
                        href={AppRoutes.destination(dest.pageURL)}
                        className="th-btn style2 th-icon"
                        title={`View All in ${dest.countryName}`}
                      >
                        View All
                        <span className="visually-hidden">{` in ${dest.countryName}`}</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
          {destinations.map((dest, i) => (
            <SwiperSlide key={dest.id}>
              <div className={`destination-box${i > 0 ? " gsap-cursor" : ""}`}>
                <div
                  className="destination-img"
                  style={{ position: "relative", height: "350px" }}
                >
                  <Image
                    src={dest.coverPhoto}
                    alt={dest.countryName}
                    fill
                    style={{ objectFit: "cover" }}
                  />
                  <div className="destination-content">
                    <div className="media-left">
                      <h4 className="box-title">
                        <a
                          href={AppRoutes.destination(dest.pageURL)}
                          title={dest.countryName}
                        >
                          {dest.countryName}
                        </a>
                      </h4>
                      <span className="destination-subtitle">
                        Top Destination
                      </span>
                    </div>
                    <div>
                      <a
                        href={AppRoutes.destination(dest.pageURL)}
                        className="th-btn style2 th-icon"
                        title={`View All in ${dest.countryName}`}
                      >
                        View All
                        <span className="visually-hidden">{` in ${dest.countryName}`}</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
}
